export const SCORE_WEIGHTS = {
  essentialSkillsQualifications: 35,
  relevantExperienceAchievements: 25,
  seniorityResponsibilities: 20,
  industryTechnicalContext: 10,
  practicalAlignment: 10,
} as const;

export type ScoreDimension = keyof typeof SCORE_WEIGHTS;

export type ScoreBreakdown = Record<ScoreDimension, number>;

export type Gap = {
  label: string;
  detail: string;
  type: 'missing' | 'not-evidenced';
};

export type Assessment = {
  score: number;
  rating: string;
  verdict: 'Apply' | 'Worth exploring' | 'Probably skip';
  verdictSummary: string;
  scoreBreakdown: ScoreBreakdown;
  strengths: string[];
  gaps: Gap[];
  watchOuts: string[];
  improvements: string[];
  nextStep: string;
  uncertainty: string;
};

export const scoreDimensionLabels: Record<ScoreDimension, string> = {
  essentialSkillsQualifications: 'Essential skills',
  relevantExperienceAchievements: 'Relevant experience',
  seniorityResponsibilities: 'Seniority & scope',
  industryTechnicalContext: 'Industry context',
  practicalAlignment: 'Practical alignment',
};

export function weightedScore(breakdown: ScoreBreakdown): number {
  const weighted = Object.entries(SCORE_WEIGHTS).reduce((total, [dimension, weight]) => {
    const value = breakdown[dimension as ScoreDimension];
    return total + value * (weight / 100);
  }, 0);

  return Math.max(0, Math.min(100, Math.round(weighted)));
}

export function ratingForScore(score: number): string {
  if (score >= 80) return 'Strong match';
  if (score >= 65) return 'Promising match';
  if (score >= 50) return 'Mixed match';
  return 'Weak match';
}

export function normaliseAssessment(value: Assessment): Assessment {
  const score = weightedScore(value.scoreBreakdown);
  return {
    ...value,
    score,
    rating: ratingForScore(score),
    strengths: value.strengths.slice(0, 5),
    gaps: value.gaps.slice(0, 5),
    watchOuts: value.watchOuts.slice(0, 4),
    improvements: value.improvements.slice(0, 3),
  };
}
